import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { PreviewActivityProvider } from "../../shared/previewActivity";

export const subjects = [
  ["math", "數學"], ["chinese", "國文"], ["english", "英文"],
  ["science", "理化科"], ["social", "社會科"],
];

// Keep inactive workspaces mounted in detached containers: drafts survive switching,
// while form IDs, labels, and keyboard focus only belong to the active workspace.
export default function SubjectWorkspace({ component: Workspace, workspaceProps = {}, subjectRequest }) {
  const [subject, setSubject] = useState("math");
  const [visited, setVisited] = useState(["math"]);
  const hostRef = useRef(null);
  const containers = useRef(new Map());
  useEffect(() => {
    if (!subjectRequest || !subjects.some(([id]) => id === subjectRequest)) return;
    setVisited((current) => current.includes(subjectRequest) ? current : [...current, subjectRequest]);
    setSubject(subjectRequest);
  }, [subjectRequest]);
  for (const id of visited) {
    if (!containers.current.has(id)) containers.current.set(id, document.createElement("div"));
  }
  useLayoutEffect(() => {
    const host = hostRef.current;
    const container = containers.current.get(subject);
    host.appendChild(container);
    return () => container.remove();
  }, [subject]);
  return <>
    <SubjectButtons aria-label="題庫科目">
      {subjects.map(([id, label]) => <button key={id} type="button"
        aria-pressed={subject === id} onClick={() => {
          setVisited(current => current.includes(id) ? current : [...current, id]);
          setSubject(id);
        }}><span>{label}</span><small>{label}題庫</small></button>)}
    </SubjectButtons>
    <div ref={hostRef} />
    {visited.map(id => createPortal(
      <PreviewActivityProvider value={subject === id && workspaceProps.routeActive !== false}>
        <Workspace {...workspaceProps} isActive={subject === id} subject={id} subjectLabel={subjects.find(item => item[0] === id)[1]} />
      </PreviewActivityProvider>,
      containers.current.get(id), id,
    ))}
  </>;
}

const SubjectButtons = styled.nav`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
  margin: 16px 0 22px;
  button {
    min-height: 82px;
    padding: 14px;
    border: 2px solid #dbe4ed;
    border-radius: 16px;
    background: #fff;
    color: #334155;
    cursor: pointer;
    font: inherit;
    display: grid;
    gap: 5px;
  }
  button span { font-size: 22px; font-weight: 750; }
  button small { font-size: 12px; opacity: .75; }
  button:hover { border-color: #507eac; }
  button[aria-pressed="true"] { background: #153f67; color: #fff; border-color: #153f67; }
  button:focus-visible { outline: 3px solid #e6ab4b; outline-offset: 3px; }

  [data-auth-theme="night"] & button {
    background: var(--app-surface, #2b2b2b);
    border-color: var(--app-border, #555555);
    color: var(--app-muted-text, #bebebe);
  }
  [data-auth-theme="night"] & button:hover {
    background: var(--app-surface-hover, #424242);
    border-color: var(--app-subtle-text, #bebebe);
  }
  [data-auth-theme="night"] & button[aria-pressed="true"] {
    background: var(--app-nav-active-bg, #4a4a4a);
    border-color: var(--app-subtle-text, #bebebe);
    color: var(--app-nav-active-text, #eeeeee);
  }
`;
